import React, { useState, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Switch,
  Radio,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  Tooltip,
  Image,
} from "antd";
import userServiceApi from "../../api/userServiceApi";
import { getRequest } from "../../api/http";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import { partnerName } from "../../store/constants";
import { EXISTING_MEMBER_QUESTIONS } from "../constants";
import PhoneInput from "react-phone-number-input";
import backArrow from "../../assets/images/back-arrow.svg";
import ytPlayBtn from "../../assets/images/ytPlayBtn.svg";
import PlayBtn from "../../assets/images/play.svg";
import SynthesiaImage from "../../assets/images/synthesia.jpg";
import ModalPlayer from "../microSites/ModalMediaPlayer";
import { getYouTubeVideoId } from "../../utils/helper";
import "./Login.scss";

const WHY_APPLY_THUMB_WIDTH = 112;
const WHY_APPLY_THUMB_HEIGHT = 68;

export default function SignupForm() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const [, setSectors] = useState([]);
  const [, setCountries] = useState([]);
  const [partnerData, setPartnerData] = useState({});
  const partnerID = localStorage.getItem("partnerID");
  const [postalCode, setPostalCode] = useState('');

  const isactiveValue = sessionStorage.getItem("isactive") ? JSON.parse(sessionStorage.getItem("isactive")) : false

  const [member, setMember] = useState(isactiveValue);
  const [allOrgTypes, setAllOrgTypes] = useState([]);
  const [whyApplyHelp, setWhyApplyHelp] = useState(null);
  const [whyApplyVideoUrl, setWhyApplyVideoUrl] = useState("");
  const [whyApplyMediaType, setWhyApplyMediaType] = useState("");
  const [whyApplyModalOpen, setWhyApplyModalOpen] = useState(false);

  const extractSynthesiaId = (url) => {
    const match = url?.match(
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
    );
    return match ? match[0] : null;
  };

  const openWhyApplyMedia = (url, mediaType) => {
    setWhyApplyMediaType(mediaType);
    setWhyApplyVideoUrl(url);
    setWhyApplyModalOpen(true);
  };

  const closeWhyApplyMedia = () => {
    setWhyApplyModalOpen(false);
    setWhyApplyVideoUrl("");
    setWhyApplyMediaType("");
  };

  const goToLogin = () => {
    if (isMobile) {
      setAlertModal(true);
      return;
    }
    navigate(`/login/${partnerID}`);
  };

  const fetchWhyApplyHelp = async () => {
    try {
      const response = await getRequest(
        userServiceApi.getHelpSectionByTab("WHY_APPLY")
      );
      if (response?.data?.length) {
        setWhyApplyHelp(response.data[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderWhyApplyThumbnail = () => {
    const { mediaLink } = whyApplyHelp || {};
    const { mediaType, url } = mediaLink || {};
    if (!mediaType || !url) return null;

    const thumbStyle = {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    };
    const wrapperStyle = {
      position: "relative",
      width: WHY_APPLY_THUMB_WIDTH,
      height: WHY_APPLY_THUMB_HEIGHT,
      borderRadius: 6,
      overflow: "hidden",
      cursor: "pointer",
      flexShrink: 0,
    };

    switch (mediaType) {
      case "SOCIAL_MEDIA": {
        const videoID = getYouTubeVideoId(url);
        if (!videoID) return null;
        const isShort = /\/shorts\//.test(url);
        return (
          <div
            style={wrapperStyle}
            onClick={() =>
              openWhyApplyMedia(
                `https://www.youtube.com/embed/${videoID}?autoplay=1&rel=0`,
                mediaType
              )
            }
          >
            <img
              src={`https://img.youtube.com/vi/${videoID}/mqdefault.jpg`}
              alt="Why apply"
              style={{
                ...thumbStyle,
                transform: isShort ? "scale(2.4)" : undefined,
              }}
            />
            <img
              src={ytPlayBtn}
              alt=""
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 24,
                height: 24,
                pointerEvents: "none",
              }}
            />
          </div>
        );
      }
      case "VIMEO_MEDIA": {
        const videoID = url?.split("/")[4];
        return (
          <div
            style={wrapperStyle}
            onClick={() => openWhyApplyMedia(url, mediaType)}
          >
            <img
              src={`https://vumbnail.com/${videoID}.jpg`}
              alt="Why apply"
              style={thumbStyle}
            />
            <img
              src={ytPlayBtn}
              alt=""
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 24,
                height: 24,
                pointerEvents: "none",
              }}
            />
          </div>
        );
      }
      case "SYNTHESIA_MEDIA": {
        const videoId = extractSynthesiaId(url);
        if (!videoId) return null;
        const thumbnail = mediaLink?.thumbnailUrl;
        return (
          <div
            style={wrapperStyle}
            onClick={() =>
              openWhyApplyMedia(
                `https://share.synthesia.io/embeds/videos/${videoId}?autoplay=1`,
                mediaType
              )
            }
          >
            <img
              src={thumbnail || SynthesiaImage}
              alt="Why apply"
              style={thumbStyle}
            />
            <img
              src={PlayBtn}
              alt=""
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 24,
                height: 24,
                pointerEvents: "none",
              }}
            />
          </div>
        );
      }
      case "IMAGE":
      case "GIF":
        return (
          <div className="signup-why-apply-image">
            <Image
              src={url}
              alt="Why apply"
              preview={{ src: url }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const getOnOrganisationType = async () => {
    try {
      const response = await getRequest(
        userServiceApi.getOnOrganisationType(partnerID)
      );
      if (response) {
        const responseList = response.data;
        setAllOrgTypes(responseList);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getOnOrganisationType();
    fetchWhyApplyHelp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPartnerDetailsData = async (partnerID) => {
    try {
      const response = await getRequest(
        userServiceApi.getPartnerDetailsLoginScreen(partnerName)

      );
      if (response && response.data) {
        setPartnerData(response.data);
        console.log('partnerData===>', response?.data);
      }
    } catch (error) { }
  };

  const fetchSectorList = async () => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const response = await getRequest(
        userServiceApi.getAllSectors(partnerID, "SECTOR"),
        {
          headers,
        }
      );
      setSectors(response.data);
    } catch (error) {
    }
  };

  const fetchCountryList = async () => {
    try {
      const response = await getRequest(userServiceApi.getAllCountries());
      setCountries(response.data);
    } catch (error) {
      // setLoading(false);
    }
  };

  const onFinish = async (values) => {
    let formData = {
      addressStreet: values?.addressStreet,
      email: values?.email,
      isActiveMember: member ?? false,
      name: values?.name,
      phone: values?.phone,
      postcode: values?.postcode,
      size: values?.size,
      typeId: values?.type,
      userName: values?.userName,
      websiteLink: values?.websiteLink,
      salesType: partnerData?.salesType?.toLowerCase(),
      partnerType: partnerData?.partnerType?.toLowerCase()
    };
    console.log('formData===>', formData);
    sessionStorage.setItem("adr", values?.addressStreet);
    sessionStorage.setItem("email", values?.email);
    sessionStorage.setItem("isactive", member ?? false);
    sessionStorage.setItem("name", values?.name);
    sessionStorage.setItem("phone", values?.phone);
    sessionStorage.setItem("postcode", values?.postcode);
    sessionStorage.setItem("size", values?.size);
    sessionStorage.setItem("userName", values?.userName);
    sessionStorage.setItem("webLink", values?.websiteLink);
    const typeName =
      allOrgTypes.find((type) => type.id === formData.typeId)?.displayName ||
      "";
    sessionStorage.setItem("typeId", values.type);
    sessionStorage.setItem("type", typeName);
    sessionStorage.setItem("salesType", partnerData?.salesType?.toLowerCase());
    sessionStorage.setItem("partnerType", partnerData?.partnerType?.toLowerCase());
    navigate("/terms", {
      state: {
        formData,
        nameType: typeName,
      },
    });
  };
  const onFinishFailed = (errorInfo) => { };

  useEffect(() => {
    const storedSwitchValue = sessionStorage.getItem("isactive");
    if (storedSwitchValue !== null) {
      // Convert the stored value to boolean if necessary
      setMember(storedSwitchValue === "true");
    }
  }, []);

  const validatePostalCode = (value) => {
    if (!value) return Promise.reject("Postal code is required");

    // Global postal code pattern
    const postalCodeRegex = /^[A-Za-z0-9\s-]{3,12}$/;

    if (!postalCodeRegex.test(value)) {
      return Promise.reject("Please enter a valid postal code");
    }

    return Promise.resolve();
  };

  const redirectHandler = () => {
    sessionStorage.clear();
    if (isMobile) {
      setAlertModal(true);
    } else {
      navigate(`/login/${partnerID}`);
    }
  };

  useEffect(() => {
    if (Object?.keys(partnerData)?.length > 0) {
      setMember(partnerData?.salesType?.toLowerCase() === "retail" ? true : isactiveValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerData])

  useEffect(() => {
    if (sessionStorage.getItem("postcode")) {
      setPostalCode(sessionStorage.getItem("postcode"));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStorage])

  useEffect(() => {
    setTimeout(() => {
      getPartnerDetailsData(partnerID);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCountryList();
    fetchSectorList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <div className="headerBannerRow">
        <div className="headerBannerCol">
          <div className="container">
            <img
              className="signup-logo"
              style={{ cursor: "pointer" }}
              src={partnerData.mediaLink}
              alt=""
              onClick={redirectHandler}
            />
            <h3 className="signUpHeading">
              Add your organisation to {partnerData.siteName}
            </h3>
          </div>
        </div>
      </div>

      <div className="changePwdContainer signup-page-container">
        <div className="signup-why-apply-toolbar">
          {/* <img
            src={backArrow}
            alt="Back to login"
            className="signup-why-apply-back"
            onClick={goToLogin}
          /> */}
          <div className="signup-why-apply-center">
            <span className="signup-why-apply-label">Why Apply?</span>
            {renderWhyApplyThumbnail()}
          </div>
        </div>
        <div className="signup-form-outer">
          <Form
            className="signup-page-form"
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 24 }}
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            initialValues={{
              type: sessionStorage.getItem("typeId")
                ? sessionStorage.getItem("typeId")
                : allOrgTypes.length > 0
                  ? allOrgTypes[0]?.id
                  : "",
              isActiveMember: isactiveValue,
              name: sessionStorage.getItem("name")
                ? sessionStorage.getItem("name")
                : "",
              addressStreet: sessionStorage.getItem("adr")
                ? sessionStorage.getItem("adr")
                : "",
              postcode: sessionStorage.getItem("postcode")
                ? sessionStorage.getItem("postcode")
                : postalCode || "",
              size: sessionStorage.getItem("size")
                ? sessionStorage.getItem("size")
                : "MICRO",
              userName: sessionStorage.getItem("userName")
                ? sessionStorage.getItem("userName")
                : "",
              email: sessionStorage.getItem("email")
                ? sessionStorage.getItem("email")
                : "",
              phone: sessionStorage.getItem("phone")
                ? sessionStorage.getItem("phone")
                : "",
              websiteLink: sessionStorage.getItem("webLink")
                ? sessionStorage.getItem("webLink")
                : "",
            }}
          >
            <Row
              className="rowBody"
              gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            ></Row>
            <div className="changePwdBody">
              {loading ? (
                <div className="data-loader">
                  <Spin tip="Loading" size="large" />
                </div>
              ) : (
                <>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item org-type-radio"
                      label="Organisation type"
                      name="type"
                      rules={[
                        {
                          required: true,
                          message: "Organisation type is required",
                        },
                      ]}
                    >
                      <Radio.Group buttonStyle="solid">
                        {allOrgTypes?.length > 0
                          ? allOrgTypes.map((type) => (
                            <Radio.Button
                              key={type.id}
                              value={type.id}
                              style={{ margin: "3px", borderRadius: "5px", borderLeft: "1px solid #d9d9d9" }}
                            >
                              {type.displayName}
                            </Radio.Button>
                          ))
                          : null}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  {
                    partnerData?.salesType?.toLowerCase() === "wholesale" && <Col className="gutter-row" xs={24} xl={24}>
                      <Form.Item
                        className="form-item member-form switchItem"
                        label={EXISTING_MEMBER_QUESTIONS[partnerData?.partnerType?.toLowerCase()] || "Are you an existing member?"}
                        name="isActiveMember"
                        rules={[
                          { required: true, message: "Active member is required" },
                        ]}
                      >
                        <div className="flexSwitch">
                          <Switch
                            className="member-form-switch"
                            checked={member}
                            onChange={(e) => {
                              if (e) {
                                setMember(true);
                              } else {
                                setMember(false);
                              }
                            }}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                          />
                          <div className="memberCondition">{`${partnerData?.name ? partnerData?.name : ""
                            }`}</div>
                        </div>
                      </Form.Item>
                    </Col>
                  }
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Organisation name"
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Organisation name is required",
                        },
                      ]}
                    >
                      <Input placeholder="Organisation name" />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Address"
                      name="addressStreet"
                      rules={[
                        {
                          required: true,
                          message: "Address is required and must be within 100 characters",
                        },
                        {
                          max: 100,
                          message: "Address cannot be more than 100 characters",
                        },
                      ]}
                    >
                      <TextArea
                        rows={4}
                        maxLength={100} // prevents typing beyond 100 chars
                        showCount
                        placeholder="Full address including postal code (You need to be in our catchment area)"
                      />
                    </Form.Item>

                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Postal code"
                      name="postcode"
                      rules={[
                        {
                          required: true,
                          validator: (_, value) => validatePostalCode(value),
                          message: "Please enter a valid postal code",
                        },
                      ]}
                    >
                      <div className="d-flex align-items-center col-12 col-md-4 col-lg-3">
                        <TextArea rows={1} placeholder="postal code"
                          style={{ width: "95%" }}
                          className="text-area"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}

                        />

                        <Tooltip title="Enter your postal code">
                          <ExclamationCircleOutlined style={{ marginLeft: 8, fontSize: "20px", color: '#3067ae', position: 'absolute', right: '-25px', top: '5px', marginRight: '20px' }} />
                        </Tooltip>

                      </div>
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Organisation size"
                      name="size"
                      rules={[
                        {
                          required: true,
                          message: "Organisation size is required",
                        },
                      ]}
                    >
                      <Radio.Group defaultValue="a" buttonStyle="solid">
                        <Radio.Button value="MICRO" style={{ margin: "3px", borderRadius: "5px", borderLeft: "1px solid #d9d9d9" }}>Micro 0-9</Radio.Button>
                        <Radio.Button value="SMALL" style={{ margin: "3px", borderRadius: "5px", borderLeft: "1px solid #d9d9d9" }}>Small 10-49</Radio.Button>
                        <Radio.Button value="MEDIUM" style={{ margin: "3px", borderRadius: "5px", borderLeft: "1px solid #d9d9d9" }}>Medium 50-249</Radio.Button>
                        <Radio.Button value="LARGE" style={{ margin: "3px", borderRadius: "5px", borderLeft: "1px solid #d9d9d9" }}>Large 249+</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Full name"
                      name="userName"
                      rules={[
                        { required: true, message: "Full name is required" },
                        {
                          pattern: /^[^0-9]*$/,
                          message: "Field does not accept numbers",
                        },
                      ]}
                    >
                      <Input placeholder="Full name" />
                    </Form.Item>

                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      name="email"
                      className="form-item"
                      label="Email"
                      normalize={(value) => value?.trim()}
                      rules={[
                        { required: true, message: "Email is required" },
                        { type: "email", message: "Enter valid email" },
                      ]}
                    >
                      <Input placeholder="Email" />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label="Phone number"
                      name="phone"
                      rules={[
                        {
                          required: true,
                          message: "Phone number is required",
                        },
                        {
                          pattern: /^\+[1-9]\d{6,14}$/,
                          message: "Enter a valid mobile number",
                        },
                      ]}
                    >
                      <PhoneInput
                        style={{ width: '100%' }}
                        className="otp-phone-input otp-input"
                        defaultCountry="GB"
                        placeholder="Enter mobile number"
                        focusInputOnCountrySelection
                        international
                      />
                    </Form.Item>
                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                    <Form.Item
                      className="form-item"
                      label={<span>Organisation website</span>}
                      name="websiteLink"
                      rules={[
                        {
                          required: true,
                          message: "Organisation website is required",
                        },
                        {
                          type: "url",
                          message: "Please enter a valid URL",
                        },
                      ]}
                    >
                      <Input placeholder="Website" required />
                    </Form.Item>

                  </Col>
                  <Col className="gutter-row" xs={24} xl={24}>
                  </Col>
                  <Col className="gutter-row submitBtn" xs={24} xl={24}>
                    <div className="signUpBtns">
                      <Button className="greenBtn signup-form-next-btn" htmlType="submit">
                        Next
                      </Button>
                    </div>
                  </Col>
                </>
              )}
            </div>
          </Form>
        </div>
        <Modal
          width={450}
          style={{ top: "30vh" }}
          open={alertModal}
          onCancel={() => setAlertModal(false)}
          footer={null}
          closable={false}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <h3>Please use a laptop or a desktop to login</h3>
          </div>
        </Modal>
        {whyApplyModalOpen ? (
          <ModalPlayer
            closeModal={closeWhyApplyMedia}
            mediaLink={whyApplyVideoUrl}
            mediaType={whyApplyMediaType}
          />
        ) : null}
      </div>
    </React.Fragment>
  );
}
